import { Component, Inject, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { DOCUMENT } from '@angular/common';

import { Subscription, tap } from 'rxjs';
import { Angulartics2GoogleTagManager } from 'angulartics2';
import { datadogRum } from '@datadog/browser-rum';

import { LoaderService, ServerTimeService } from '@tablesready/common-ui';
import { FAVICON_ICONS } from '@tablesready/common-ui';
import { SocketsService } from './services';
import { AlertsService, IAlertTypes } from '@tablesready/frontend-alerts-bar';
import { AppVariables, APP_VARIABLES_TOKEN, AppVariablesService } from '@tablesready/frontend-variables';

@Component({
  selector: 'tablesready-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy {
  path: string;
  private subscriptions = new Subscription();

  @ViewChild('socketErrorMessageTmpl') private socketErrorMessageTmpl: TemplateRef<any>;

  constructor(
    private readonly loaderService: LoaderService,
    private readonly alertsService: AlertsService,
    private readonly socketService: SocketsService,
    private readonly angulartics2GoogleTagManager: Angulartics2GoogleTagManager,
    private readonly appVariablesService: AppVariablesService,
    @Inject(APP_VARIABLES_TOKEN) private readonly appVariables: AppVariables,
    @Inject(DOCUMENT) private document: HTMLDocument,
    private readonly serverTimeService: ServerTimeService,
  ) {
    loaderService.setLoading(true);
    angulartics2GoogleTagManager.startTracking();

    let applicationId = '';
    let env = 'develop';
    let clientToken = '';

    try {
      applicationId = process.env.DD_RUM_APPID;
      env = process.env.DD_RUM_ENV;
      clientToken = process.env.DD_RUM_CLIENTTOKEN;
    } catch (e) {
      console.log({ e });
    }

    datadogRum.init({
      applicationId,
      clientToken,
      site: 'datadoghq.com',
      service: 'tablesready',
      version: '0.1.2',
      env,
      sessionSampleRate: 20,
      sessionReplaySampleRate: 5,
      trackUserInteractions: true,
      defaultPrivacyLevel: 'mask-user-input',
      allowedTracingUrls: [
        'https://api.tablesready.com',
        'https://host.tablesready.com',
        'https://tablesready-production.herokuapp.com',
        'https://tablesready-staging.herokuapp.com',
      ],
    });
  }

  ngOnInit(): void {
    this.document.addEventListener('visibilitychange', this.visibilityChangeListener);

    const connectSocketsSub = this.socketService.onConnectionError.subscribe(() => {
      const isBodyTemplate = !!this.socketErrorMessageTmpl;
      const connectionErrorMessage = 'It looks like there\'s a problem with your connection.';

      this.alertsService.add({
        id: 'network-connection-lost',
        duration: 7 * 24 * 60 * 60 * 1000,
        time: Date.now(),
        type: IAlertTypes.danger,
        heading: 'Oops.',
        body: connectionErrorMessage,
        bodyTemplate: isBodyTemplate ? this.socketErrorMessageTmpl : undefined,
        actions: [
          {
            label: 'Click here to reload',
            action: (): void => {
              this.reload();
            },
          },
        ],
      });
    });

    const authViaSocketsSub = this.socketService.onAuthenticated.subscribe(() => {
      this.alertsService.clear();
    });

    this.path = this.appVariables.WHITELABEL_BRAND.toLowerCase();
    this.generateFaviconIcons(this.path);
    this.subscriptions.add(connectSocketsSub);
    this.subscriptions.add(authViaSocketsSub);

    this.socketService.connect();

    const offsetTimeSubscription = this.serverTimeService.offset$
      .pipe(
        tap(offset => {
          // @ts-ignore
          Date.setOffset(offset);
        }),
      )
      .subscribe();
    this.subscriptions.add(offsetTimeSubscription);
  }

  generateFaviconIcons(path) {
    const head = this.document.getElementsByTagName('head')[0],
      fragment = this.document.createDocumentFragment(),
      links = [...FAVICON_ICONS];

    links.forEach((link) => {
      const tag = this.document.createElement("link");
      tag.setAttribute('rel', link.rel);
      tag.setAttribute('href', `./assets/img/${path}/${link.name}`);
      tag.setAttribute('sizes', link.sizes);
      tag.setAttribute('media', link.media);
      tag.setAttribute('color', link.color);
      fragment.appendChild(tag);
    });

    head.appendChild(fragment);
  }

  ngOnDestroy() {
    this.document.removeEventListener('visibilitychange', this.visibilityChangeListener);
    this.subscriptions.unsubscribe();
    this.socketService.disconnect();
  }

  reload() {
    window.location.reload();
  }

  private readonly visibilityChangeListener = (): void => {
    this.alertsService.clear();

    if (!this.document.hidden) {
      this.appVariablesService.getAppVariables().subscribe();
    }
  };
}
